import { createSignal, onMount, For, Show } from 'solid-js';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { showConfirm } from './Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { useInventoryApi, type StorageInfo } from '../services/inventoryApi';
import { MAIN_SLOTS, HOTBAR_SLOTS } from './BotInventory';
import WindowSlot, { WindowSlotRow } from './inventory/WindowSlot';
import { isInventorySlotEmpty, type InventorySlot, type InventorySnapshot } from '../models/inventorySlot';
import Text from './Text';
import StorageSelect from './StorageSelect';
import inventoryStyles from './BotInventory.module.css';
import styles from './DumpInventoryPanel.module.css';
import { firstError } from '../api/envelope';

type DumpMode = 'Storage' | 'Drop';
type DumpSelection = 'All' | 'NonRepairable' | 'Manual';

interface DumpInventoryPanelProps {
    botId: string;
    snapshot: InventorySnapshot | null;
    disabled?: boolean;
}

const itemKeyOf = (s: InventorySlot) => `${s.itemId}:${s.damage}`;

export default function DumpInventoryPanel(props: DumpInventoryPanelProps) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const inv = useInventoryApi();

    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [mode, setMode] = createSignal<DumpMode>('Storage');
    const [selection, setSelection] = createSignal<DumpSelection>('All');
    const [storageId, setStorageId] = createSignal('');
    const [selectedItemKeys, setSelectedItemKeys] = createSignal<string[]>([]);
    const [running, setRunning] = createSignal(false);

    onMount(async () => {
        const list = await inv.fetchStorages();
        setStorages(list);
        if (!storageId() && list.length > 0) setStorageId(list[0].id);
    });

    function toggleSlot(slot: InventorySlot | undefined) {
        if (props.disabled || running() || isInventorySlotEmpty(slot)) return;
        const key = itemKeyOf(slot!);
        setSelectedItemKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    }

    async function run() {
        if (mode() === 'Storage' && !storageId()) {
            toast?.error(t('DUMPINV_SELECT_STORAGE'));
            return;
        }
        if (selection() === 'Manual' && selectedItemKeys().length === 0) {
            toast?.error(t('DUMPINV_SELECT_ITEMS'));
            return;
        }
        if (mode() === 'Drop') {
            if (!await showConfirm(t('DUMPINV_DROP_CONFIRM'), true)) return;
        }

        setRunning(true);
        const res = await api.botTask.enqueueTask(props.botId, {
            plugin: 'Storage',
            task: 'DumpInventory',
            args: {
                mode: mode(),
                selection: selection(),
                storageId: mode() === 'Storage' ? storageId() : '',
                itemKeys: selection() === 'Manual' ? selectedItemKeys().join(',') : '',
            },
        });
        setRunning(false);
        if (isOk(res.data)) {
            toast?.info(t('DUMPINV_STARTED'));
        } else {
            toast?.error(firstError(res.data) ?? t('DUMPINV_START_FAILED'));
        }
    }

    function ToggleSlot(slotProps: { slot: InventorySlot | undefined }) {
        const empty = () => isInventorySlotEmpty(slotProps.slot);
        const selected = () => !empty() && selectedItemKeys().includes(itemKeyOf(slotProps.slot!));

        return (
            <WindowSlot
                item={slotProps.slot}
                toggleable={!props.disabled && !empty()}
                selected={selected()}
                onClick={() => toggleSlot(slotProps.slot)}
            />
        );
    }

    return (
        <div class={styles.card}>
            <h2><i class="fa-solid fa-box-open" /> <Text val="DUMPINV_TITLE" /></h2>

            <div class={styles.formGrid}>
                <label class={styles.field}>
                    <span class={styles.fieldLabel}><Text val="DUMPINV_MODE" /></span>
                    <select
                        class={styles.input}
                        value={mode()}
                        onChange={e => setMode(e.currentTarget.value as DumpMode)}
                        disabled={props.disabled || running()}
                    >
                        <option value="Storage">{t('DUMPINV_MODE_STORAGE')}</option>
                        <option value="Drop">{t('DUMPINV_MODE_DROP')}</option>
                    </select>
                </label>

                <label class={styles.field}>
                    <span class={styles.fieldLabel}><Text val="DUMPINV_SELECTION" /></span>
                    <select
                        class={styles.input}
                        value={selection()}
                        onChange={e => setSelection(e.currentTarget.value as DumpSelection)}
                        disabled={props.disabled || running()}
                    >
                        <option value="All">{t('DUMPINV_SELECTION_ALL')}</option>
                        <option value="NonRepairable">{t('DUMPINV_SELECTION_NONREPAIRABLE')}</option>
                        <option value="Manual">{t('DUMPINV_SELECTION_MANUAL')}</option>
                    </select>
                </label>

                <Show when={mode() === 'Storage'}>
                    <label class={styles.field}>
                        <span class={styles.fieldLabel}><Text val="DUMPINV_STORAGE" /></span>
                        <StorageSelect
                            storages={storages()}
                            value={storageId()}
                            onChange={setStorageId}
                            placeholder={t('DUMPINV_SELECT_STORAGE_OPTION')}
                            disabled={props.disabled || running()}
                        />
                    </label>
                </Show>
            </div>

            <Show when={selection() === 'Manual'}>
                <div class={styles.field}>
                    <span class={styles.fieldLabel}><Text val="DUMPINV_ITEMS" /></span>
                    <p class={styles.hint}><Text val="DUMPINV_ITEMS_HINT" /></p>
                    <Show
                        when={props.snapshot}
                        fallback={<div class={styles.hint}><Text val="BOT_INVENTORY_WAITING" /></div>}
                    >
                        {(snap) => (
                            <>
                                <div class={inventoryStyles.sectionLabel}><Text val="BOT_INVENTORY_MAIN" /></div>
                                <WindowSlotRow>
                                    <For each={MAIN_SLOTS}>{(i) => <ToggleSlot slot={snap().slots[i]} />}</For>
                                </WindowSlotRow>
                                <div class={`${inventoryStyles.sectionLabel} ${styles.hotbarLabel}`}>
                                    <Text val="HOTBAR" />
                                </div>
                                <WindowSlotRow>
                                    <For each={HOTBAR_SLOTS}>{(i) => <ToggleSlot slot={snap().slots[i]} />}</For>
                                </WindowSlotRow>
                            </>
                        )}
                    </Show>
                </div>
            </Show>

            <div class={styles.actions}>
                <button class="btn btn-primary" onClick={run} disabled={props.disabled || running()}>
                    <Show when={running()} fallback={<><i class="fa-solid fa-play" /> <Text val="DUMPINV_RUN" /></>}>
                        <i class="fa-solid fa-spinner fa-spin" /> <Text val="DUMPINV_RUN" />
                    </Show>
                </button>
            </div>
        </div>
    );
}
